import { quizSetSchema, type MediaAssetDescriptor, type QuizQuestion, type QuizSet } from "@quizshow/protocol";
import { createQuizDocument, parseQuizDocument } from "./quiz-document";
import { convertLegacyClassic, isLegacyClassic } from "./legacy-classic";
import { loadMedia, saveMedia, saveQuiz } from "./storage/quiz-store";

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(",")[1] ?? "");
    reader.onerror = () => reject(reader.error ?? new Error("Medium konnte nicht exportiert werden."));
    reader.readAsDataURL(blob);
  });
}

function base64ToBlob(base64: string, mimeType: string): Blob {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return new Blob([bytes], { type: mimeType });
}

export async function exportQuizFile(quiz: QuizSet): Promise<void> {
  const media: Array<{ descriptor: MediaAssetDescriptor; base64: string }> = [];
  for (const descriptor of quiz.mediaAssets) {
    const blob = await loadMedia(descriptor.mediaId);
    if (!blob) throw new Error(`Medium "${descriptor.fileName}" fehlt in der lokalen Bibliothek.`);
    media.push({ descriptor, base64: await blobToBase64(blob) });
  }
  const exportedDocument = createQuizDocument(quiz, media);
  const blob = new Blob([JSON.stringify(exportedDocument)], { type: "application/vnd.quizshow-studio+json" });
  const url = URL.createObjectURL(blob);
  const anchor = window.document.createElement("a");
  anchor.href = url;
  anchor.download = `${quiz.title.toLowerCase().replace(/[^a-z0-9äöüß]+/gi, "-").replace(/^-|-$/g, "") || "quiz"}.qss`;
  anchor.click();
  URL.revokeObjectURL(url);
}

function legacyError(value: unknown): Error {
  const record = typeof value === "object" && value ? (value as Record<string, unknown>) : {};
  const hints = ["questions", "categories", "quiz", "rounds"].filter((key) => key in record);
  return new Error(
    `Legacy-Format erkannt${hints.length ? ` (${hints.join(", ")})` : ""}, aber ohne Beispieldatei nicht sicher konvertierbar. ` +
      "Bitte eine anonymisierte Originaldatei bereitstellen; der Adapter ist vorbereitet.",
  );
}

function remapQuestionMedia<T extends QuizSet>(quiz: T, mediaIds: Map<string, string>): T {
  const remapQuestion = (question: QuizQuestion): QuizQuestion => ({
    ...question,
    answers: question.answers.map((answer) => ({
      ...answer,
      mediaId: answer.mediaId ? mediaIds.get(answer.mediaId) ?? answer.mediaId : undefined,
    })),
    media: {
      ...question.media,
      imageMediaId: question.media.imageMediaId ? mediaIds.get(question.media.imageMediaId) : undefined,
      audioMediaId: question.media.audioMediaId ? mediaIds.get(question.media.audioMediaId) : undefined,
      backgroundMediaId: question.media.backgroundMediaId ? mediaIds.get(question.media.backgroundMediaId) : undefined,
    },
  });
  const common = {
    ...quiz,
    quizId: crypto.randomUUID(),
    updatedAt: new Date().toISOString(),
    mediaAssets: quiz.mediaAssets.map((asset) => ({ ...asset, mediaId: mediaIds.get(asset.mediaId) ?? asset.mediaId })),
    ranks: quiz.ranks.map((rank) => ({
      ...rank,
      imageMediaId: rank.imageMediaId ? mediaIds.get(rank.imageMediaId) ?? rank.imageMediaId : undefined,
    })),
  };
  if (quiz.moduleId === "quizboard") {
    return {
      ...common,
      categories: quiz.categories.map((category) => ({
        ...category,
        cells: category.cells.map((cell) => ({ ...cell, question: remapQuestion(cell.question) })),
      })),
    } as T;
  }
  return { ...common, questions: quiz.questions.map(remapQuestion) } as T;
}

export async function importQuizFile(file: File): Promise<QuizSet> {
  let value: unknown;
  try {
    value = JSON.parse(await file.text()) as unknown;
  } catch {
    throw new Error("Die Datei enthält kein lesbares Quizshow-Studio- oder JSON-Format.");
  }

  const directQuiz = quizSetSchema.safeParse(value);
  if (directQuiz.success) {
    if (directQuiz.data.mediaAssets.length) {
      throw new Error("Dieser JSON-Import verweist auf Medien, enthält aber keine eingebetteten Dateien. Bitte das .qss-Format verwenden.");
    }
    const imported = remapQuestionMedia(directQuiz.data, new Map());
    await saveQuiz(imported);
    return imported;
  }

  if (isLegacyClassic(value)) {
    const converted = convertLegacyClassic(value, file.name);
    for (const entry of converted.media) {
      await saveMedia(entry.descriptor.mediaId, entry.blob);
    }
    await saveQuiz(converted.quiz);
    return converted.quiz;
  }

  let document;
  try {
    document = parseQuizDocument(value);
  } catch (error) {
    if ((value as { format?: unknown })?.format !== "quizshow-studio") throw legacyError(value);
    throw error;
  }
  const media = document.media;
  const totalBytes = media.reduce((total, entry) => total + (entry?.descriptor?.bytes ?? 0), 0);
  if (totalBytes > 100 * 1024 * 1024) throw new Error("Der Import überschreitet das Medienlimit von 100 MB.");
  const mediaIds = new Map(document.quiz.mediaAssets.map((asset) => [asset.mediaId, crypto.randomUUID()]));
  for (const entry of media) {
    if (!entry?.descriptor || typeof entry.base64 !== "string") {
      throw new Error("Ein Medium im Import ist unvollständig.");
    }
    const blob = base64ToBlob(entry.base64, entry.descriptor.mimeType);
    if (blob.size !== entry.descriptor.bytes) {
      throw new Error(`Medium "${entry.descriptor.fileName}" ist beschädigt.`);
    }
    const remappedId = mediaIds.get(entry.descriptor.mediaId);
    if (!remappedId) throw new Error(`Medium "${entry.descriptor.fileName}" wird vom Quiz nicht referenziert.`);
    await saveMedia(remappedId, blob);
  }
  const imported = remapQuestionMedia(document.quiz, mediaIds);
  await saveQuiz(imported);
  return imported;
}
